import React, { useState, useRef, useEffect } from 'react';
import { Control, Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import { useAppSelector } from '../../../store/hooks/StoreHooks';

export interface OptionItem<T = any> {
    label: string;
    value: T;
    disabled?: boolean;
};

export interface SelectInputProps<TValue = string | number> {
    name: string;
    label?: string;
    placeholder?: string;
    className?: string;
    options: OptionItem<TValue>[];
    multiple?: boolean;
    disabled?: boolean;
    helperText?: string;
    error?: string;
    control?: Control<FieldValues>;
    rules?: RegisterOptions;
    value?: TValue | TValue[];
    onChange?: (value: TValue | TValue[]) => void;
    enableSearch?: boolean;
    enableDeselect?: boolean;
    searchPlaceholder?: string;
    [key: string]: any;
    dropdownDirection?: "down" | "up";
};

const SelectElementInner = <TValue extends string | number = string>({
    name,
    options,
    multiple,
    isDisabled,
    error,
    getClasses,
    enableSearch,
    searchPlaceholder,
    enableDeselect,
    placeholder,
    currentValue,
    handleFieldChange,
    onChange,
    onBlur,
    dropdownDirection,
    rest,
}: {
    name: string;
    options: OptionItem<TValue>[];
    multiple: boolean;
    isDisabled: boolean;
    error?: string;
    getClasses: (hasError?: boolean) => string;
    enableSearch: boolean;
    searchPlaceholder: string;
    enableDeselect: boolean;
    placeholder: string;
    currentValue: any;
    handleFieldChange: (v: any) => void;
    onChange?: (value: TValue | TValue[]) => void;
    onBlur?: () => void;
    dropdownDirection?: "down" | "up";
    rest: Record<string, any>;
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const filteredOptions = options?.filter(option =>
        option?.label && typeof option.label === 'string' &&
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Apply default option when value is empty
    useEffect(() => {
        const isEmptySingle = currentValue === undefined || currentValue === null || currentValue === '';
        const isEmptyMulti = multiple && (!Array.isArray(currentValue) || currentValue.length === 0);

        if ((isEmptySingle || isEmptyMulti) && options?.length) {
            if (multiple) {
                const defaultVals = options.filter(opt => (opt as any).default).map(opt => opt.value);
                if (defaultVals.length > 0) {
                    handleFieldChange(defaultVals);
                    onChange?.(defaultVals);
                }
            } else {
                const defaultOpt = options.find(opt => (opt as any).default);
                if (defaultOpt) {
                    handleFieldChange(defaultOpt.value);
                    onChange?.(defaultOpt.value);
                }
            }
        }
    }, [currentValue, options, multiple, handleFieldChange, onChange]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setFocusedIndex(-1);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && enableSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen, enableSearch]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!isOpen) {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setFocusedIndex(0);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusedIndex(prev =>
                    prev > 0 ? prev - 1 : filteredOptions.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
                    handleOptionSelect(filteredOptions[focusedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setFocusedIndex(-1);
                setSearchTerm('');
                triggerRef.current?.focus();
                break;
        }
    };

    const handleOptionSelect = (option: OptionItem<TValue>) => {
        if (option.disabled) return;

        if (multiple) {
            const currentArray = Array.isArray(currentValue) ? currentValue : [];
            const isSelected = currentArray.includes(option.value as any);
            const newValue = isSelected
                ? currentArray.filter(v => v !== option.value)
                : [...currentArray, option.value];
            handleFieldChange(newValue);
            onChange && onChange(newValue);
        } else {
            handleFieldChange(option.value);
            onChange && onChange(option.value);
            setIsOpen(false);
            setFocusedIndex(-1);
            setSearchTerm('');
        }
    };

    const handleDeselect = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newValue = multiple ? [] : ('' as TValue);
        handleFieldChange(newValue);
        onChange && onChange(newValue);
    };

    const getDisplayValue = () => {
        if (multiple) {
            const selectedOptions = options?.filter(opt =>
                ((opt as any).default === true) || (Array.isArray(currentValue) && currentValue.includes(opt.value as any))
            );
            if (selectedOptions.length === 0) return placeholder;
            if (selectedOptions.length === 1) return selectedOptions[0].label;
            return `${selectedOptions.length} selected`;
        } else {
            const isEmpty = currentValue === undefined || currentValue === null || currentValue === '';
            if (isEmpty) return placeholder;
            const selectedOption = options?.find(opt =>
                opt.value === currentValue || ((currentValue === undefined || currentValue === null || currentValue === '') && (opt as any).default === true)
            );
            return selectedOption ? selectedOption.label : placeholder;
        }
    };

    const isSelected = (option: OptionItem<TValue>) => {
        if (multiple) {
            return Array.isArray(currentValue) && currentValue.includes(option.value as any);
        }
        return currentValue === option.value;
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                ref={triggerRef}
                type='button'
                id={name}
                disabled={isDisabled}
                aria-invalid={!!error}
                aria-expanded={isOpen}
                aria-haspopup='listbox'
                className={getClasses(!!error)}
                onClick={() => !isDisabled && setIsOpen(prev => !prev)}
                onKeyDown={handleKeyDown}
                onBlur={onBlur}
                {...rest}
            >
                <span className={`block truncate text-left ${isDisabled ? 'text-neutral-200' : ''}`}>
                    {getDisplayValue()}
                </span>
                <span className='absolute inset-y-0 right-0 flex items-center pr-3'>
                    {enableDeselect &&
                        currentValue &&
                        (multiple
                            ? Array.isArray(currentValue) && currentValue.length > 0
                            : true) && (
                            <button
                                type='button'
                                onClick={handleDeselect}
                                className='mr-2 p-1 hover:bg-gray-600 rounded-full transition-colors'
                                aria-label='Clear selection'
                            >
                                <svg
                                    className='w-4 h-4 text-neutral-400 hover:text-neutral-200'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                >
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth={2}
                                        d='M6 18L18 6M6 6l12 12'
                                    />
                                </svg>
                            </button>
                        )}
                    <svg
                        className={`w-5 h-5 text-neutral-400 transition-transform duration-200 pointer-events-none ${
                            isOpen ? "rotate-180" : ""
                        }`}
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                    >
                        <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M19 9l-7 7-7-7'
                        />
                    </svg>
                </span>
            </button>

            {isOpen && (
                <div
                    className={`absolute z-50 w-full ${dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-darkcolor-800 border border-darkcolor-400 rounded-[8px] shadow-lg max-h-60 overflow-auto`}
                >
                    {enableSearch && (
                        <div className='p-2 border-b border-darkcolor-400'>
                            <input
                                ref={searchInputRef}
                                type='text'
                                placeholder={searchPlaceholder}
                                value={searchTerm}
                                className='w-full px-3 py-2 text-[16px] bg-darkcolor-700 border border-darkcolor-400 rounded-[6px] text-neutral-25 placeholder-neutral-400 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500'
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setFocusedIndex(0);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                                        e.preventDefault();
                                    }
                                }}
                            />
                        </div>
                    )}
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((option, index) => {
                            const selected = isSelected(option);
                            const focused = index === focusedIndex;

                            return (
                                <div
                                    key={String(option.value)}
                                    className={`px-3 py-2 text-[16px] cursor-pointer transition-colors duration-150 ${
                                        option.disabled
                                            ? "text-neutral-500 cursor-not-allowed"
                                            : selected
                                            ? "text-primary-400 bg-primary-900/20"
                                            : focused
                                            ? "text-neutral-25 bg-darkcolor-600"
                                            : "text-neutral-25 hover:bg-darkcolor-600"
                                    } ${index === 0 ? "rounded-t-[8px]" : ""} ${
                                        index === filteredOptions.length - 1
                                            ? "rounded-b-[8px]"
                                            : ""
                                    }`}
                                    onClick={() => handleOptionSelect(option)}
                                    onMouseEnter={() => setFocusedIndex(index)}
                                >
                                    <div className='flex items-center justify-between'>
                                        <span className='block truncate'>{option.label}</span>
                                        {multiple && selected && (
                                            <svg
                                                className='w-4 h-4 text-primary-400'
                                                fill='currentColor'
                                                viewBox='0 0 20 20'
                                            >
                                                <path
                                                    fillRule='evenodd'
                                                    d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                                                    clipRule='evenodd'
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div className='px-3 py-2 text-neutral-400 text-center'>
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const Select = <TValue extends string | number = string>({
    name,
    label,
    placeholder = 'Select an option',
    className,
    options,
    multiple = false,
    disabled,
    helperText,
    error,
    control,
    rules,
    value,
    onChange,
    enableSearch = true,
    enableDeselect = false,
    searchPlaceholder = 'Search options...',
    dropdownDirection,
    ...rest
}: SelectInputProps<TValue>) => {
    const { customerEnabled, customerCreated } = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);
    const isDisabled = disabled || (customerEnabled && !customerCreated) || projectReadOnly;
    
    const baseClasses = 'flex px-[12px] py-[10px] rounded-[8px] border border-primary-500 w-full bg-transparent text-neutral-25 text-[16px] hover:border-primary-600 focus:ring-1 focus:ring-primary-400 focus:border-primary-400 hover:ring-1 hover:ring-primary-400 ring-1 ring-darkcolor-400';
    const getClasses = (hasError?: boolean) => {
        const errorClasses = hasError ? ' border-red-500 hover:border-red-500 focus:ring-red-500 focus-within:border-red-500' : '';
        const disabledClasses = isDisabled ? ' opacity-50 cursor-not-allowed' : '';
        const customerDisabledClasses = (customerEnabled && !customerCreated) || projectReadOnly ? ' text-neutral-200 cursor-not-allowed hover:border-neutral-600' : '';
        return `${baseClasses}${errorClasses}${disabledClasses}${customerDisabledClasses}`.trim();
    };

    return (
        <div className={className ? className : "w-full"}>
            {label && (
                <label className={`flex justify-start items-start mb-1 text-sm ${isDisabled ? 'text-neutral-200' : 'text-neutral-25'}`} style={{ cursor: 'default' }}>
                    {label}
                    {(rest.required || rules?.required) && <span className="text-error-400 ml-1">*</span>}
                </label>
            )}
            {control ? (
                <Controller
                    name={name}
                    control={control}
                    rules={rules}
                    render={({ field }) => (
                        <SelectElementInner
                            name={name}
                            options={options}
                            multiple={multiple ?? false}
                            isDisabled={!!isDisabled}
                            error={error}
                            getClasses={getClasses}
                            enableSearch={enableSearch ?? true}
                            searchPlaceholder={searchPlaceholder}
                            enableDeselect={enableDeselect ?? false}
                            placeholder={placeholder}
                            currentValue={field.value}
                            handleFieldChange={field.onChange}
                            onChange={onChange}
                            onBlur={field.onBlur}
                            dropdownDirection={dropdownDirection}
                            rest={rest}
                        />
                    )}
                />
            ) : (
<SelectElementInner
                    name={name}
                    options={options}
                    multiple={multiple ?? false}
                    isDisabled={!!isDisabled}
                    error={error}
                    getClasses={getClasses}
                    enableSearch={enableSearch ?? true}
                    searchPlaceholder={searchPlaceholder}
                    enableDeselect={enableDeselect ?? false}
                    placeholder={placeholder}
                    currentValue={value as any}
                    handleFieldChange={() => {}}
                    onChange={onChange}
                    dropdownDirection={dropdownDirection}
                    rest={rest}
                />
            )}
            {(error || helperText) && (
                <span className={`mt-1 block text-xs ${error ? 'text-red-500' : 'text-neutral-400'}`}>
                    {error ?? helperText}
                </span>
            )}
        </div>
    );
};

export default Select;


